import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  DialogTitle,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import { verifyUser, OtpVerify } from '../../services/advisorLeadService';
import { showToast } from '../../components/common/Util/Util';
import { useSelector } from 'react-redux';
import { ip } from '../../Store/userSlice';

const MobileVerificationModal = ({ open, onClose, onVerificationSuccess }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpError, setOtpError] = useState(false);
  const [timer, setTimer] = useState(60);
  const [resendVisible, setResendVisible] = useState(false);
  const IpAddress = useSelector(ip)
  const [locationData, setLocationData] = useState({
    latitude: '',
    longitude: '',
    ip: ''
  });

  const resetState = useCallback(() => {
    setMobileNumber('');
    setShowOTP(false);
    setRequestData(null);
    setOtp(['', '', '', '']);
    setTimer(60);
    setResendVisible(false);
    setOtpError(false);
    setLoading(false);
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      resetState();
      // Get location and IP when modal opens
      getLocationAndIP();
    }
  }, [open, resetState]);

  const getLocationAndIP = async () => {
    try {
      // Get IP address
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      
      // Get geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocationData({
              latitude: position.coords.latitude.toString(),
              longitude: position.coords.longitude.toString(),
              ip: ipData.ip
            });
          },
          (error) => {
            console.error('Geolocation error:', error);
            setLocationData(prev => ({
              ...prev,
              ip: ipData.ip
            }));
          }
        );
      } else {
        setLocationData(prev => ({
          ...prev,
          ip: ipData.ip
        }));
      }
    } catch (error) {
      console.error('Error getting location/IP:', error);
    }
  };

  // Handle countdown timer
  useEffect(() => {
    let interval;
    if (timer > 0 && showOTP) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setResendVisible(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer, showOTP]);

  const handleMobileSubmit = async () => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      showToast('Please enter a valid 10-digit mobile number', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        mobileNumber,
        ip: locationData.ip || '',
        longitude: locationData.longitude || '',
        latitude: locationData.latitude || '',
        screen: 'eligibility'
      };

      const response = await verifyUser(payload);
      if (response?.statusCode === 200) {
        setRequestData(payload);
        setShowOTP(true);
        setTimer(60);
        setResendVisible(false);
      } else {
        showToast(response?.message || 'Failed to send OTP', 'error');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      showToast('Failed to send OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (e, index) => {
    const value = e.target.value;
    if (value.length <= 1 && !isNaN(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 3) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleVerifyOTP = async () => {
    const enteredOtp = otp.join('');
    if (!enteredOtp || enteredOtp.length !== 4) {
      setOtpError(true);
      showToast('Please enter the complete OTP', 'error');
      return;
    }

    setLoading(true);
    try {
      const verifyOtpRequest = {
        mobileNumber: requestData.mobileNumber,
        mobileOtp: enteredOtp
      };

      const response = await OtpVerify(verifyOtpRequest);
      if (response?.statusCode === 200 && response?.message === "OTP Verified") {
        showToast('OTP verified successfully', 'success');
        onVerificationSuccess(mobileNumber);
      } else {
        showToast('Invalid OTP, please enter the valid OTP', 'error');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      showToast('Failed to verify OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const response = await verifyUser(requestData);
      if (response?.statusCode === 200) {
        showToast('OTP sent successfully', 'success');
        setOtp(['', '', '', '']);
        setTimer(60);
        setResendVisible(false);
      } else {
        showToast(response?.message || 'Failed to send OTP', 'error');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      showToast('Failed to send OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!showOTP ? (
            <PhoneIcon sx={{ color: '#004AAD' }} />
          ) : (
            <LockIcon sx={{ color: '#004AAD' }} />
          )}
          <Typography variant="h6" sx={{ 
            color: '#004AAD',
            fontWeight: 600,
            fontSize: '1.25rem',
            letterSpacing: '-0.01em'
          }}>
            {!showOTP ? 'Verify Mobile Number' : 'Enter OTP'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: 3
        }}>
          {!showOTP ? (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ 
                fontSize: '0.95rem',
                lineHeight: 1.5
              }}>
                Please enter your mobile number to proceed with the eligibility check
              </Typography>
              <TextField
                fullWidth
                label="Mobile Number"
                variant="outlined"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10 digit mobile number"
                disabled={loading}
                InputProps={{
                  sx: {
                    borderRadius: 1.5,
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(0, 74, 173, 0.2)'
                    }
                  }
                }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleMobileSubmit}
                disabled={loading || mobileNumber.length !== 10}
                sx={{
                  backgroundColor: "#004AAD",
                  borderRadius: 1.5,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: "#003c8f",
                    boxShadow: '0 4px 12px rgba(0, 74, 173, 0.2)'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Number'}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ 
                fontSize: '0.95rem',
                lineHeight: 1.5,
                textAlign: 'center'
              }}>
                Please enter the verification code sent to<br />
                <Typography component="span" sx={{ 
                  color: '#004AAD',
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  +91 {mobileNumber}
                </Typography>
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 1.5, 
                justifyContent: 'center',
                my: 2
              }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    id={`otp-${index}`}
                    variant="outlined"
                    value={digit}
                    onChange={(e) => handleOTPChange(e, index)}
                    error={otpError && !digit}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: 'center',
                        width: '48px',
                        height: '48px',
                        fontSize: '1.5rem',
                        padding: '8px',
                        borderRadius: '12px'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&.Mui-focused': {
                          boxShadow: '0 0 0 2px rgba(0, 74, 173, 0.2)'
                        }
                      }
                    }}
                    disabled={loading}
                  />
                ))}
              </Box>
              <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyOTP}
                disabled={loading || otp.join('').length !== 4}
                sx={{
                  backgroundColor: "#004AAD",
                  borderRadius: 1.5,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: "#003c8f",
                    boxShadow: '0 4px 12px rgba(0, 74, 173, 0.2)'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                {resendVisible ? (
                  <Button
                    onClick={handleResend}
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      color: '#004AAD',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 74, 173, 0.08)'
                      }
                    }}
                  >
                    Resend OTP
                  </Button>
                ) : (
                  <Typography sx={{ 
                    fontSize: '0.875rem', 
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5
                  }}>
                    Resend available in <Box component="span" sx={{ 
                      color: '#004AAD',
                      fontWeight: 600,
                      minWidth: '24px'
                    }}>{timer}s</Box>
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MobileVerificationModal; 